import './AccountItem.less';
import React, { useState, useEffect } from 'react';
import { Avatar, Typography } from 'antd';
import { AccountType } from '../../enums/account';
import { Auth0UserInfo } from '../../interfaces/account';
import { Team } from '../../interfaces/team';
import { getTeamName, getUserRole } from '../../services/account.service';
import classNames from 'classnames';

export interface AccountItemObject {
  type: AccountType;
  id?: string | null;
  name?: string;
  email?: string;
  logoUrl?: string | null;
}

interface AccountItemProps {
  className?: string;
  type: AccountType;
  object: Auth0UserInfo | Team;
  showType?: boolean;
  showLicense?: boolean;
  showRole?: boolean;
  showEmail?: boolean;
  disabled?: boolean;
}

const AccountItem: React.FC<AccountItemProps> = ({
  className,
  type,
  object,
  showType = false,
  showLicense = false,
  showRole = false,
  showEmail = true,
  disabled = false,
}) => {
  const [currentAccount, setCurrentAccount] =
    useState<AccountItemObject | null>(null);
  // const [license, setLicense] = useState<OrganizationLicense | null>(null);

  // useEffect(() => {
  //   setLicense(getLicense());
  // }, []);

  useEffect(() => {
    if (type === AccountType.Team) {
      const orgObj = object as Team;
      setCurrentAccount({
        type: AccountType.Team,
        id: orgObj.id,
        name: getTeamName(orgObj),
        // logoUrl: orgObj.logo_url,
      });
    } else if (type === AccountType.Personal) {
      const userObj = object as Auth0UserInfo;
      setCurrentAccount({
        type: AccountType.Personal,
        name: userObj?.name || 'Personal Account',
        email: userObj?.email || '',
        logoUrl: userObj?.logoUrl || null,
      });
    }
  }, [type, object]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map((word) => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getAvatarColor = (name: string) => {
    if (disabled) {
      return '#888888';
    }
    const colors = [
      '#1890ff',
      '#52c41a',
      '#faad14',
      '#f5222d',
      '#722ed1',
      '#13c2c2',
    ];
    const hash = name
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  const generateDefaultAvatar = (currentAccount: AccountItemObject) => {
    if (currentAccount.logoUrl) {
      return <Avatar src={currentAccount.logoUrl} />;
    }
    const name = currentAccount.name || 'Account';
    return (
      <Avatar
        style={{
          backgroundColor: getAvatarColor(currentAccount.id || name),
        }}
      >
        {getInitials(name)}
      </Avatar>
    );
  };

  const shouldShowEmail =
    showEmail &&
    currentAccount?.email &&
    currentAccount.email !== currentAccount.name;

  const role =
    type === AccountType.Team
      ? getUserRole((object as Team)?.current_user)
      : null;

  return (
    <div
      className={classNames(
        'account-item-container',
        disabled && 'disabled',
        className
      )}
    >
      {currentAccount && (
        <>
          <div className="account-item-avatar">
            {generateDefaultAvatar(currentAccount)}
          </div>
          <div
            className={classNames('account-item-title', { disabled: disabled })}
          >
            <Typography.Text strong>{currentAccount.name}</Typography.Text>
            {(shouldShowEmail || showType || showLicense || showRole) && (
              <>
                {shouldShowEmail && (
                  <div className="email">{currentAccount.email}</div>
                )}
                {showType && (
                  <Typography.Text
                    type="secondary"
                    className="account-item-subtitle"
                  >
                    {currentAccount.type === AccountType.Team
                      ? 'Team Account'
                      : 'Personal Account'}
                  </Typography.Text>
                )}
                {/* {showLicense && license && (
                  <Typography.Text
                    type="secondary"
                    className="account-item-subtitle"
                  >
                    License:{' '}
                    <span
                      className={`license-info ${
                        license === OrganizationLicense.Guest
                          ? 'license-guest'
                          : license === OrganizationLicense.Full
                            ? 'license-full'
                            : ''
                      }`}
                    >
                      {license}
                    </span>
                  </Typography.Text>
                )} */}
                {showRole && role && (
                  <Typography.Text
                    type="secondary"
                    className="account-item-subtitle"
                  >
                    Role: {role}
                  </Typography.Text>
                )}
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AccountItem;
